import { useState } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { getDeviceUUID } from './SignalRating';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const ICS_COLORS = {
  A:'#44aa66', B:'#81c784', C:'#c4a563', D:'#ff9933', E:'#ff5555', F:'#8a9bbf'
};
const ICS_RELIABILITY = {
  A:'Completely reliable', B:'Usually reliable', C:'Fairly reliable',
  D:'Not usually reliable', E:'Unreliable', F:'Unknown reliability'
};
const ICS_CREDIBILITY = {
  1:'Confirmed', 2:'Probably true', 3:'Possibly true',
  4:'Doubtfully true', 5:'Improbable', 6:'Cannot be judged'
};

function PEBar({ value, color, label }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
        <span style={{ color:'#666', fontSize:10 }}>{label}</span>
        <span style={{ color, fontSize:10, fontWeight:'bold' }}>{value}%</span>
      </div>
      <div style={{ height:4, backgroundColor:'rgba(255,255,255,0.07)', borderRadius:2 }}>
        <div style={{ height:'100%', width:`${value}%`, backgroundColor:color,
          borderRadius:2, transition:'width 0.4s ease' }} />
      </div>
    </div>
  );
}

export function PEWidget({ hypothesisId, hypothesis, timeframeDays, timeframeBasis,
  forwardPE, peNarrative, baseRate, baseRateBasis,
  sourceReliability, sourceCredibility, sourceBasis, briefingDate }) {

  const [myEstimate, setMyEstimate]   = useState(null);
  const [sliderVal,  setSliderVal]    = useState(50);
  const [direction,  setDirection]    = useState('');
  const [submitted,  setSubmitted]    = useState(false);
  const [aggregate,  setAggregate]    = useState(null);
  const [expanded,   setExpanded]     = useState(false);
  const { track } = useAnalytics();
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError]  = useState(null);

  const deviceUUID = getDeviceUUID();

  // Resolution date
  const resolutionDate = briefingDate ? (() => {
    const d = new Date(briefingDate);
    d.setDate(d.getDate() + (timeframeDays || 180));
    return d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
  })() : null;

  const peColor = forwardPE >= 75 ? '#ff5555' : forwardPE >= 55 ? '#ff9933' :
                  forwardPE >= 40 ? '#c4a563' : '#8a9bbf';

  const timeframeLabel = timeframeBasis === 'explicit' ? '(explicit)' :
                         timeframeBasis === 'inferred' ? '(inferred)' : '(baseline)';

  const handleSubmit = async () => {
    if (submitting || !hypothesisId) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/hypotheses/${hypothesisId}/estimate`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ device_uuid: deviceUUID, pe_estimate: sliderVal, direction })
      });
      const data = await res.json();
      if (data.aggregate) setAggregate(data.aggregate);
      setMyEstimate(sliderVal);
      setSubmitted(true);
      track('pe_estimate_submitted', { hypothesis_id: hypothesisId, estimate: sliderVal, direction });
    } catch(e) {
      setSubmitError('Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (!hypothesis) return null;

  return (
    <div style={{ marginTop:10, padding:'10px 12px',
      backgroundColor:'rgba(196,165,99,0.04)',
      border:'1px solid rgba(196,165,99,0.15)',
      borderRadius:6 }}>

      {/* Header row */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
        gap:8, marginBottom:8, cursor:'pointer' }} onClick={() => setExpanded(e=>!e)}>
        <div style={{ flex:1 }}>
          <div style={{ color:'#555', fontSize:9, fontWeight:'bold', letterSpacing:'0.6px', marginBottom:3 }}>
            HYPOTHESIS {timeframeDays && `· ${timeframeDays}d ${timeframeLabel}`}
            {resolutionDate && <span style={{ color:'#444' }}> · resolves {resolutionDate}</span>}
          </div>
          <div style={{ color:'#c8c9cd', fontSize:12, lineHeight:1.5 }}>{hypothesis}</div>
        </div>
        <div style={{ flexShrink:0, textAlign:'center' }}>
          <div style={{ color:peColor, fontSize:20, fontWeight:'bold', lineHeight:1 }}>{forwardPE}%</div>
          <div style={{ color:'#444', fontSize:9 }}>Platform PE</div>
        </div>
        <span style={{ color:'#444', fontSize:10, flexShrink:0 }}>{expanded?'▲':'▼'}</span>
      </div>

      {/* PE bar */}
      <PEBar value={forwardPE} color={peColor} label="Platform probability estimate" />

      {/* Expanded detail */}
      {expanded && (
        <div style={{ marginTop:10, paddingTop:8, borderTop:'1px solid rgba(255,255,255,0.05)' }}>

          {/* PE narrative */}
          {peNarrative && (
            <p style={{ color:'#666', fontSize:11, lineHeight:1.6, marginBottom:8, fontStyle:'italic' }}>
              {peNarrative}
            </p>
          )}

          {/* Base rate */}
          {baseRate !== null && baseRate !== undefined && (
            <div style={{ marginBottom:8 }}>
              <PEBar value={Math.round(baseRate*100)} color='#8a9bbf' label="Historical base rate" />
              {baseRateBasis && <div style={{ color:'#444', fontSize:10, marginTop:2 }}>{baseRateBasis}</div>}
            </div>
          )}

          {/* ICS source assessment */}
          {sourceReliability && sourceCredibility && (
            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
              <span style={{ fontSize:10, fontWeight:'bold', color:'#555', letterSpacing:'0.4px' }}>SOURCE</span>
              <span style={{ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:'bold',
                backgroundColor: ICS_COLORS[sourceReliability]+'22',
                color: ICS_COLORS[sourceReliability],
                border:`1px solid ${ICS_COLORS[sourceReliability]}55` }}>
                {sourceReliability}{sourceCredibility}
              </span>
              <span style={{ color:'#555', fontSize:10 }}>
                {ICS_RELIABILITY[sourceReliability]} · {ICS_CREDIBILITY[sourceCredibility]}
              </span>
              {sourceBasis && <span style={{ color:'#444', fontSize:10, fontStyle:'italic' }}>{sourceBasis}</span>}
            </div>
          )}

          {/* User PE estimate */}
          {!submitted ? (
            <div style={{ padding:'10px', backgroundColor:'rgba(255,255,255,0.02)',
              borderRadius:5, border:'1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ color:'#c4a563', fontSize:10, fontWeight:'bold',
                letterSpacing:'0.5px', marginBottom:8 }}>YOUR PROBABILITY ESTIMATE</div>

              {/* Slider */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <span style={{ color:'#444', fontSize:10, flexShrink:0 }}>0%</span>
                <input type="range" min="0" max="100" value={sliderVal}
                  onChange={e => setSliderVal(parseInt(e.target.value))}
                  style={{ flex:1, accentColor:'#c4a563' }} />
                <span style={{ color:'#444', fontSize:10, flexShrink:0 }}>100%</span>
                <span style={{ color:'#c4a563', fontWeight:'bold', fontSize:14,
                  minWidth:40, textAlign:'right' }}>{sliderVal}%</span>
              </div>

              {/* Direction buttons */}
              <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                {['likely','monitor','unlikely'].map(d => (
                  <button key={d} onClick={() => setDirection(d)} style={{
                    flex:1, padding:'5px 0', borderRadius:4, cursor:'pointer',
                    fontSize:11, fontWeight:'bold', textTransform:'capitalize',
                    backgroundColor: direction===d ? 'rgba(196,165,99,0.15)' : 'transparent',
                    border:`1px solid ${direction===d ? '#c4a563' : 'rgba(255,255,255,0.1)'}`,
                    color: direction===d ? '#c4a563' : '#555'
                  }}>{d}</button>
                ))}
              </div>

              <button onClick={handleSubmit} disabled={submitting || !direction}
                style={{ width:'100%', padding:'7px 0', borderRadius:4,
                  cursor: submitting||!direction ? 'not-allowed' : 'pointer',
                  fontSize:11, fontWeight:'bold',
                  backgroundColor: direction ? 'rgba(196,165,99,0.12)' : 'transparent',
                  border:`1px solid ${direction ? '#c4a563' : 'rgba(255,255,255,0.1)'}`,
                  color: direction ? '#c4a563' : '#444' }}>
                {submitting ? 'Submitting…' : 'Submit estimate'}
              </button>
              {!direction && (
                <div style={{ color:'#444', fontSize:10, textAlign:'center', marginTop:4, fontStyle:'italic' }}>
                  Select Likely, Monitor, or Unlikely to submit
                </div>
              )}
              {submitError && (
                <div style={{ color:'#ff6b6b', fontSize:10, textAlign:'center', marginTop:4 }}>
                  {submitError}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding:'8px 10px', backgroundColor:'rgba(196,165,99,0.06)',
              borderRadius:5, border:'1px solid rgba(196,165,99,0.2)' }}>
              <div style={{ color:'#c4a563', fontSize:11, fontWeight:'bold', marginBottom:4 }}>
                ✓ Your estimate: {myEstimate}% · {direction}
              </div>
              {aggregate && (
                <div style={{ color:'#666', fontSize:11 }}>
                  Community avg: {aggregate.avg_pe}% across {aggregate.total} estimates
                </div>
              )}
              <div style={{ color:'#444', fontSize:10, marginTop:4, fontStyle:'italic' }}>
                Outcome tracked until {resolutionDate}. Your Analyst Score updates when resolved.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
